import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { commitShowExportImport, previewShowExportImport } from "@/api/manager";
import { DialogDescription, DialogTitle } from "@/components/ui/dialog";

type Decision = { include?: boolean; timezone?: string | null };
type PreviewRow = {
  row_key: string;
  ref: { filename: string; row: number };
  status: "ok" | "needs_review" | "rejected" | "duplicate_in_upload";
  action?: "new" | "unchanged" | "updated" | "conflict";
  reasons: string[];
  event_id: string | null;
  name?: string;
  venue: string | null;
  city: string | null;
  state: string | null;
  local_date: string;
  local_time: string | null;
  resolved_timezone?: string | null;
  timezone_candidates?: string[];
  place_flags?: string[];
  can_include?: boolean;
  included?: boolean;
  changed_fields?: string[];
  conflicts?: Record<string, unknown>;
  possible_matches?: { name: string | null; venue: string | null; source: string; is_manual: boolean }[];
};
type Preview = {
  files: { filename: string; rows: number }[];
  counts: Record<string, number>;
  rows: PreviewRow[];
  artist_name_mismatch: boolean;
  preview_token: string;
};

const REASONS: Record<string, string> = {
  timezone_unresolved: "Time zone can't be determined — choose one",
  duplicate_event_id_in_export: "Bandsintown reused this event ID for another listing",
  event_id_previously_imported_differently: "This event ID was imported differently before",
  possible_match_api_synced_show: "May match a show synced from Bandsintown",
  possible_existing_show_same_day: "You already have a show this day",
  missing_start_time: "No start time in the export",
  dst_nonexistent_local_time: "Start time falls in a daylight-saving gap",
  dst_ambiguous_local_time: "Start time is ambiguous (daylight-saving change)",
  invalid_start_date: "Invalid start date",
  invalid_start_time: "Invalid start time",
  invalid_event_id: "Missing or invalid Event Id",
  missing_venue_and_city: "No venue or city",
  same_show_in_both_files: "Same show appears in both files",
};
const FLAGS: Record<string, string> = {
  livestream: "Livestream",
  private_event: "Private",
  hidden_location: "Hidden location",
  no_physical_address: "No street address",
  festival_or_multiday: "Festival / multi-day",
  do_not_announce: "Not announced",
};

function errorMessage(err: unknown) {
  const payload = (err as { payload?: { detail?: { message?: string } | string } })?.payload;
  const detail = payload?.detail;
  if (detail && typeof detail === "object" && detail.message) return detail.message;
  if (typeof detail === "string") return detail;
  return (err as Error)?.message || "Something went wrong.";
}

function statusLabel(row: PreviewRow) {
  if (row.status === "rejected") return "Rejected";
  if (row.status === "duplicate_in_upload") return "Duplicate";
  if (row.status === "needs_review") return row.included ? `Review · ${row.action === "new" ? "will add" : "included"}` : "Needs review";
  return { new: "New", unchanged: "Already imported", updated: "Will update", conflict: "Conflict" }[row.action || "new"];
}

export function ShowImportDialog({ artistId, onImported, onDirtyChange }: { artistId?: string | null; onImported: () => void; onDirtyChange?: (dirty: boolean) => void }) {
  const [files, setFiles] = useState<File[]>([]);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [decisions, setDecisions] = useState<Record<string, Decision>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmName, setConfirmName] = useState(false);
  const [done, setDone] = useState<Record<string, number> | null>(null);
  const [filter, setFilter] = useState<"attention" | "all">("attention");
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  // Only the newest preview may be shown/committed; older responses can arrive late.
  const sequence = useRef(0);
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => { onDirtyChange?.(files.length > 0 && !done); }, [files, done, onDirtyChange]);
  useEffect(() => () => { if (debounce.current) clearTimeout(debounce.current); }, []);
  useEffect(() => {
    if (!files.length || done) return;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [files, done]);

  const runPreview = useCallback(async (nextFiles: File[], nextDecisions: Record<string, Decision>) => {
    const id = ++sequence.current;
    setBusy(true);
    setError(null);
    try {
      const result = await previewShowExportImport(nextFiles, nextDecisions, artistId);
      if (id === sequence.current) setPreview(result);
    } catch (err) {
      if (id !== sequence.current) return;
      setPreview(null);
      setError((err as { code?: string }).code === "timeout" ? "The server took too long to check your files. Your choices are kept — try again." : errorMessage(err));
    } finally {
      if (id === sequence.current) setBusy(false);
    }
  }, [artistId]);

  const addFiles = (list: FileList | null) => {
    if (!list) return;
    const next = [...files, ...Array.from(list)].slice(-2);
    setFiles(next);
    setDecisions({});
    setDone(null);
    void runPreview(next, {});
  };

  const decide = (key: string, change: Decision) => {
    const next = { ...decisions, [key]: { ...decisions[key], ...change } };
    setDecisions(next);
    // The preview token covers these choices; batch quick clicks into one re-check.
    setBusy(true);
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => void runPreview(files, next), 400);
  };

  const commit = async () => {
    if (!preview || busy) return;
    sequence.current += 1;
    setBusy(true);
    setError(null);
    try {
      const response = await commitShowExportImport(files, decisions, preview.preview_token, { confirmArtistName: confirmName }, artistId);
      setDone(response.result);
      setPreview(null);
      onImported();
    } catch (err) {
      setError(errorMessage(err));
      if ((err as { status?: number }).status === 409) void runPreview(files, decisions);
    } finally {
      setBusy(false);
    }
  };

  const visible = useMemo(() => (preview?.rows || []).filter(row =>
    filter === "all" || row.status !== "ok" || row.action === "conflict" || row.action === "updated"), [preview, filter]);
  const c = preview?.counts;
  const willWrite = c ? c.new + c.updated + c.conflicts : 0;

  return (
    <div className="space-y-4 text-sm">
      <div>
        <DialogTitle className="text-lg font-semibold">Import from Bandsintown</DialogTitle>
        <DialogDescription className="text-muted-foreground">
          Add your show history to Shows. Imported shows stay private to your workspace.
        </DialogDescription>
      </div>
      <details className="rounded-xl border border-border/60 bg-background/60 px-4 py-3">
        <summary className="cursor-pointer font-semibold">How to export from Bandsintown</summary>
        <ol className="mt-2 list-decimal space-y-1 pl-5 text-muted-foreground">
          <li>Sign in to Bandsintown for Artists and open <strong>Events</strong>.</li>
          <li>On the <strong>Upcoming</strong> tab, choose <strong>Export</strong> and download the file.</li>
          <li>Switch to the <strong>Past</strong> tab and export again.</li>
          <li>Drop both files here (CSV or XLSX, either order). Don't edit the columns.</li>
        </ol>
      </details>

      <div
        role="button"
        tabIndex={0}
        aria-label="Choose Bandsintown export files"
        onClick={() => inputRef.current?.click()}
        onKeyDown={e => (e.key === "Enter" || e.key === " ") && inputRef.current?.click()}
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={e => { e.preventDefault(); setDragging(false); addFiles(e.dataTransfer.files); }}
        className={`rounded-xl border-2 border-dashed px-4 py-6 text-center transition ${dragging ? "border-primary bg-primary/5" : "border-border/60 hover:border-primary/60"}`}
      >
        <p className="font-semibold">Drag your past and upcoming exports here, or click to choose</p>
        <p className="text-xs text-muted-foreground">Up to 2 files · CSV or XLSX · 5 MB each</p>
        <input ref={inputRef} type="file" multiple hidden accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          data-testid="show-import-input" onChange={e => { addFiles(e.target.files); e.target.value = ""; }} />
      </div>
      {files.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 text-xs">
          {files.map(f => <span key={f.name + f.size} className="rounded-full border border-border/60 px-2 py-1">{f.name}</span>)}
          <button type="button" className="underline" onClick={() => { setFiles([]); setPreview(null); setDecisions({}); setError(null); }}>Clear</button>
        </div>
      )}
      {busy && <p role="status" className="text-muted-foreground">Checking files…</p>}
      {error && files.length > 0 && !busy && <button type="button" className="text-xs underline" onClick={() => void runPreview(files, decisions)}>Try again</button>}
      {error && <p role="alert" className="rounded-xl border border-red-500/40 bg-red-500/10 px-3 py-2">{error}</p>}
      {done && (
        <p role="status" className="rounded-xl border border-emerald-500/40 bg-emerald-500/10 px-3 py-2">
          Imported: {done.created} new, {done.updated} updated, {done.unchanged} unchanged
          {done.conflicts ? `, ${done.conflicts} with conflicts (your edits were kept)` : ""}. {done.skipped_review} held for review, {done.rejected} rejected — these were not saved.
        </p>
      )}

      {preview && c && (
        <>
          <dl className="grid grid-cols-2 gap-2 sm:grid-cols-4" aria-label="Import summary">
            {[["Performances", c.total], ["New", c.new], ["Already imported", c.existing], ["Will update", c.updated],
              ["Conflicts", c.conflicts], ["Needs review", c.needs_review], ["Rejected", c.rejected], ["Duplicates across files", c.duplicates_in_upload]]
              .map(([label, value]) => (
                <div key={label as string} className="rounded-xl border border-border/60 px-3 py-2">
                  <dt className="text-xs text-muted-foreground">{label}</dt>
                  <dd className="text-lg font-semibold">{value}</dd>
                </div>))}
          </dl>
          {preview.artist_name_mismatch && (
            <label className="flex items-start gap-2 rounded-xl border border-amber-500/40 bg-amber-500/10 px-3 py-2">
              <input type="checkbox" checked={confirmName} onChange={e => setConfirmName(e.target.checked)} />
              <span>This export's artist name doesn't match this workspace. Import anyway?</span>
            </label>
          )}
          <div className="flex items-center gap-3 text-xs">
            <button type="button" className={filter === "attention" ? "font-semibold underline" : ""} onClick={() => setFilter("attention")}>Needs attention</button>
            <button type="button" className={filter === "all" ? "font-semibold underline" : ""} onClick={() => setFilter("all")}>All rows</button>
          </div>
          <div className="max-h-[45vh] overflow-auto rounded-xl border border-border/60">
            <table className="w-full text-left text-xs">
              <thead className="sticky top-0 bg-background">
                <tr className="border-b border-border/60">
                  <th className="px-3 py-2">Date</th><th className="px-3 py-2">Show</th><th className="px-3 py-2">Status</th><th className="px-3 py-2">Details</th>
                </tr>
              </thead>
              <tbody>
                {visible.length === 0 && <tr><td colSpan={4} className="px-3 py-4 text-center text-muted-foreground">Nothing needs attention.</td></tr>}
                {visible.map(row => (
                  <tr key={row.row_key} className="border-b border-border/40 align-top" data-testid="show-import-row">
                    <td className="whitespace-nowrap px-3 py-2">{row.local_date}{row.local_time ? ` ${row.local_time}` : ""}</td>
                    <td className="px-3 py-2">
                      <p className="font-semibold">{row.venue || row.name}</p>
                      <p className="text-muted-foreground">{[row.city, row.state].filter(Boolean).join(", ")} · {row.ref.filename} row {row.ref.row}</p>
                      {!!row.place_flags?.length && <p className="mt-1 flex flex-wrap gap-1">{row.place_flags.map(f => <span key={f} className="rounded-full border border-border/60 px-2">{FLAGS[f] || f}</span>)}</p>}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2">{statusLabel(row)}</td>
                    <td className="space-y-1 px-3 py-2">
                      {row.reasons.map(r => <p key={r}>{REASONS[r] || r}</p>)}
                      {row.possible_matches?.map((m, i) => <p key={i} className="text-muted-foreground">Existing: {m.venue || m.name} ({m.is_manual ? "entered by you" : m.source})</p>)}
                      {row.action === "updated" && <p>Changes: {row.changed_fields?.join(", ")}</p>}
                      {row.action === "conflict" && <p>Bandsintown differs on {Object.keys(row.conflicts || {}).join(", ")} — your edits are kept.</p>}
                      {row.status === "needs_review" && row.reasons.includes("timezone_unresolved") && (
                        <select aria-label="Time zone" disabled={busy} className="rounded border border-border/60 bg-background px-2 py-1"
                          value={decisions[row.row_key]?.timezone || ""} onChange={e => decide(row.row_key, { timezone: e.target.value || null })}>
                          <option value="">Choose time zone…</option>
                          {row.timezone_candidates?.map(z => <option key={z} value={z}>{z.replace("America/", "").replace("_", " ")}</option>)}
                        </select>
                      )}
                      {row.status === "needs_review" && (
                        <label className="flex items-center gap-2">
                          <input type="checkbox" disabled={!row.can_include || busy} checked={!!row.included}
                            onChange={e => decide(row.row_key, { include: e.target.checked })} />
                          <span>{row.can_include ? "Import this show" : "Choose a time zone to import"}</span>
                        </label>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs text-muted-foreground">Nothing is saved until you confirm. Rows needing review are skipped unless you include them.</p>
            <button type="button" onClick={() => void commit()}
              disabled={busy || willWrite + (c.included_review || 0) === 0 || (preview.artist_name_mismatch && !confirmName)}
              className="rounded-lg bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground transition hover:opacity-90 disabled:opacity-50">
              Confirm import
            </button>
          </div>
        </>
      )}
    </div>
  );
}
